import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import { X, Upload } from 'lucide-react';
import { uploadUserPhotoToS3 } from 'utils/s3UploadHelper';
import { getError } from 'utils/appHelpers';
import './VirtualTryOn.scss';

/**
 * Photo Upload Modal for Virtual Try-On
 * Simplified: Only allows full body photo upload
 */
const PhotoUploadModal = ({ isOpen, onClose, onPhotoUpload }) => {
  const { t } = useTranslation();
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState(null);

  // Always use full-body-front type (simplified)
  const photoType = 'full-body-front';

  const handleFileChange = e => {
    const file = e.target.files?.[0];
    if (file) {
      handleFile(file);
    }
  };

  const handleFile = file => {
    if (file && file.type.startsWith('image/')) {
      setSelectedFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreviewUrl(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };
  const handleDragOver = e => {
    e.preventDefault();
    setIsDragging(true);
  };
  const handleDragLeave = e => {
    e.preventDefault();
    setIsDragging(false);
  };
  const handleDrop = e => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files[0];
    handleFile(file);
  };

  const handleUpload = async () => {
    if (selectedFile && previewUrl) {
      setIsUploading(true);
      setUploadError(null);

      try {
        // Upload to S3 with user-specific folder structure
        const photoData = await uploadUserPhotoToS3(selectedFile, photoType);

        // Store in localStorage as backup/cache with photoPath for API
        const existingPhotos = JSON.parse(localStorage.getItem('virtualTryOnPhotos') || '[]');
        const localPhotoData = {
          ...photoData,
          dataUrl: previewUrl, // Keep preview for immediate display
        };
        existingPhotos.push(localPhotoData);
        localStorage.setItem('virtualTryOnPhotos', JSON.stringify(existingPhotos));

        // Notify parent component
        onPhotoUpload(photoData);
        handleClose();
      } catch (error) {
        console.error('Photo upload failed:', error);
        setUploadError(getError(error));
      } finally {
        setIsUploading(false);
      }
    }
  };

  const handleClose = () => {
    setSelectedFile(null);
    setPreviewUrl(null);
    setIsDragging(false);
    setUploadError(null);
    onClose();
  };

  const handleOverlayClick = event => {
    if (event.target === event.currentTarget) {
      handleClose();
    }
  };

  const handleOverlayKeyDown = event => {
    if (['Escape', 'Enter', ' '].includes(event.key)) {
      event.preventDefault();
      handleClose();
    }
  };

  // Early return if modal is not open
  if (!isOpen) return null;

  const modalContent = (
    <div
      className='photo-upload-modal-overlay'
      onClick={handleOverlayClick}
      onKeyDown={handleOverlayKeyDown}
      role='button'
      tabIndex={0}
      aria-label='Close modal'
    >
      <div className='photo-upload-modal' role='dialog' aria-modal='true'>
        <button className='photo-upload-modal__close' onClick={handleClose} aria-label='Close'>
          <X size={20} />
        </button>

        <div className='photo-upload-modal__header'>
          <h2 className='photo-upload-modal__title'>{t('Upload Your Photo')}</h2>
          <p className='photo-upload-modal__subtitle'>{t('Upload a full body photo for virtual try-on')}</p>
        </div>

        <div className='photo-upload-modal__body'>
          {/* Upload Area */}
          <div
            className={`photo-upload-modal__dropzone ${isDragging ? 'is-dragging' : ''} ${
              previewUrl ? 'has-preview' : ''
            }`}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
          >
            {!previewUrl ? (
              <>
                <Upload className='photo-upload-modal__upload-icon' size={40} />
                <p className='photo-upload-modal__upload-text'>{t('Drag & drop your photo here')}</p>
                <p className='photo-upload-modal__upload-or'>{t('or')}</p>
                <label className='photo-upload-modal__upload-btn'>
                  <input type='file' accept='image/*' onChange={handleFileChange} style={{ display: 'none' }} />
                  {t('Browse Files')}
                </label>
              </>
            ) : (
              <div className='photo-upload-modal__preview'>
                <img src={previewUrl} alt='Preview' className='photo-upload-modal__preview-image' />
                <button
                  className='photo-upload-modal__change-btn'
                  onClick={() => {
                    setSelectedFile(null);
                    setPreviewUrl(null);
                  }}
                >
                  {t('Change Photo')}
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Error Message */}
        {uploadError && (
          <div className='photo-upload-modal__error'>
            <p>{uploadError}</p>
          </div>
        )}

        {/* Footer with Actions */}
        <div className='photo-upload-modal__footer'>
          <button
            className='photo-upload-modal__btn photo-upload-modal__btn--secondary'
            onClick={handleClose}
            disabled={isUploading}
          >
            {t('Cancel')}
          </button>
          <button
            className='photo-upload-modal__btn photo-upload-modal__btn--primary'
            onClick={handleUpload}
            disabled={!selectedFile || isUploading}
          >
            {isUploading ? t('Uploading...') : t('Upload & Try On')}
          </button>
        </div>
      </div>
    </div>
  );

  // Render modal at document body level using Portal
  return createPortal(modalContent, document.body);
};
PhotoUploadModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onPhotoUpload: PropTypes.func.isRequired,
};
export default PhotoUploadModal;
